import { useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Label, Paragraph, Text, TextButton } from '@zillow/constellation-10';

import { useSelector } from '../../../../selectors/useSelector';

import { RECS, REVIEW_PAGE_SECTION_RECS } from '../../../../constants/RecommendationsConstants';

import Add3DToursCSEvents from '../../../../model/ClickstreamEvent/Add3DToursCSEvents';

import RecommendationsAlert from '../../../RecommendationsAlert';
import RecommendationsLearnMoreContent from '../../../RecommendationsLearnMoreContent';
import RecommendationIconBulb from '../../../RecommendationIconBulb';
import ListingTourFormField from '../../ListingTour/components/ListingTourFormField';
import PhotoUploadFormField from '../../AddPhotos/components/PhotoUploadFormField';
import VideoFormField from '../../Video';

import { reviewSectionStyles, StyledIconPlaceholder, StyledReviewSectionTitle, StyledReviewTitleRow } from '../styles';

const { MEDIA } = REVIEW_PAGE_SECTION_RECS;

const MediaContent = ({ onRecsClick }: { onRecsClick?: () => void }) => {
  const { currentProperty, isMultifamily, recommendations, user } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      isMultifamily: state.properties.currentProperty.isMultifamily,
      recommendations: state.properties.recommendations,
      user: state.user,
    }),
    shallowEqual,
  );

  const [displayRecsModal, setDisplayRecsModal] = useState<boolean | string>(false);

  return (
    <div data-testid="MediaContent" data-group-title="Media">
      {isMultifamily && <RecommendationsAlert sectionName={REVIEW_PAGE_SECTION_RECS.MEDIA} onRecsClick={onRecsClick} />}
      <Box css={reviewSectionStyles} data-testid="AddPhotos">
        <Box css={{ display: 'flex', alignItems: 'center', marginBottom: '400' }}>
          {!isMultifamily && (
            <StyledIconPlaceholder>
              {recommendations[MEDIA][RECS.PHOTOS] && (
                <RecommendationIconBulb
                  removeFlex={true}
                  onClick={() => {
                    setDisplayRecsModal(RECS.PHOTOS);
                  }}
                />
              )}
            </StyledIconPlaceholder>
          )}
          <StyledReviewSectionTitle>
            <Label required={isMultifamily} size="lg">
              {isMultifamily ? 'Building photos' : 'Photos'}
            </Label>
            {!isMultifamily && (
              <TextButton textStyle="body" onClick={() => setDisplayRecsModal(RECS.PHOTOS)}>
                Learn more
              </TextButton>
            )}
          </StyledReviewSectionTitle>
        </Box>

        <StyledReviewTitleRow>
          {!isMultifamily && <StyledIconPlaceholder />}
          <PhotoUploadFormField />
        </StyledReviewTitleRow>
      </Box>

      <Box css={isMultifamily ? reviewSectionStyles : undefined} data-testid="AddListingTour">
        <Box css={{ display: 'flex', alignItems: 'center', marginBottom: '200' }}>
          {!isMultifamily && (
            <StyledIconPlaceholder>
              {recommendations[MEDIA][RECS.VIRTUAL_TOUR] && (
                <RecommendationIconBulb
                  removeFlex={true}
                  data-event-id={Add3DToursCSEvents.LearnMore.data_event_id}
                  onClick={() => {
                    Add3DToursCSEvents.LearnMore({ user, currentProperty });
                    setDisplayRecsModal(RECS.VIRTUAL_TOUR);
                  }}
                />
              )}
            </StyledIconPlaceholder>
          )}
          <StyledReviewSectionTitle>
            <Text textStyle="body-bold">{isMultifamily ? 'Amenity tours' : '3D Tour'}</Text>
            {!isMultifamily && (
              <TextButton
                textStyle="body"
                data-event-id={Add3DToursCSEvents.LearnMore.data_event_id}
                onClick={() => {
                  Add3DToursCSEvents.LearnMore({ user, currentProperty });
                  setDisplayRecsModal(RECS.VIRTUAL_TOUR);
                }}
              >
                Learn more
              </TextButton>
            )}
          </StyledReviewSectionTitle>
        </Box>

        <Box css={{ display: 'flex' }}>
          {!isMultifamily && <StyledIconPlaceholder />}
          <ListingTourFormField />
        </Box>
      </Box>

      {isMultifamily && (
        <Box data-testid="AddVideoLink">
          <Paragraph textStyle="body-bold" css={{ marginBottom: '400' }}>
            Video
          </Paragraph>
          <VideoFormField />
        </Box>
      )}

      {typeof displayRecsModal === 'string' && (
        <RecommendationsLearnMoreContent
          onClose={() => setDisplayRecsModal(false)}
          recommendationType={displayRecsModal}
        />
      )}
    </div>
  );
};

export default MediaContent;
